import { httpClient } from './httpClient';
import { CreateTicketFormData } from '../types/ticket';
import { Ticket } from '../types/ticket';
import { formatDateForApi } from '../utils/dateUtils';

// export interface Ticket {
//   id: number;
//   title: string;
//   description: string;
//   status: string;
//   priority: string;
//   created_at: string;
//   updated_at: string;
//   created_by: User;
//   assigned_to?: User;
//   source_name?: string;
//   group_name?: string;
//   type_name?: string;
//   category_name?: string;
//   customer_name: string;
//   phone_number?: string;
//   is_public: boolean;
//   tags: Tag[];
//   comments: Comment[];
// }

export interface TicketQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  priority?: string;
  assigned_to?: string;
  search_criteria?: string;
  search_value?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  tags?: string[];
  requestedBy?: string[];
  assignedTo?: string[];
}

export interface TicketResponse {
  tickets: Ticket[];
  totalCount: number;
  totalPages: number;
  currentPage?: number;
  limit?: number;
  stats: {
    total: number;
    new: number;
    processing: number;
    resolved: number;
    closed: number;
    assigned: number;
    unassigned: number;
    active: number;
    open: number;
  };
}

export const ticketService = {
  async createTicket(data: CreateTicketFormData): Promise<Ticket> {
    const requestData = {
      title: data.title,
      description: data.description,
      customer_name: data.customerName?.trim() || '',
      phone_number: data.phoneNumber || null,
      customer_id: data.customerId || null,
      priority: data.priority,
      source_id: parseInt(data.source) || null,
      group_id: parseInt(data.group) || null,
      type_id: parseInt(data.type) || null,
      category_id: parseInt(data.category) || null,
      tags: Array.isArray(data.tags) ? data.tags : (data.tags ? [data.tags] : []),
      is_public: data.isPublic,
      assigned_to_id: data.assignedTo ? parseInt(data.assignedTo) : null,
      updated_by_id: data.updatedById || null,
      form_template_id: (data as any).form_template_id || null,
      form_response: (data as any).form_response || null
    };

    const response = await httpClient.post<Ticket>('/api/tickets', requestData);
    return response;
  },

  async getTicket(id: string | number): Promise<Ticket> {
    // Ensure id is a valid number
    const numericId = typeof id === 'string' ? parseInt(id) : id;
    if (isNaN(numericId)) {
      throw new Error('Invalid ticket ID: must be a valid number');
    }
    const response = await httpClient.get<Ticket>(`/api/tickets/${numericId}`);
    return response;
  },

  async getTickets(params?: TicketQueryParams): Promise<Ticket[] | TicketResponse> {
    if (params) {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.tags) queryParams.append('tags', params.tags.join(','));

      const queryString = queryParams.toString();
      const url = `/api/tickets${queryString ? `?${queryString}` : ''}`;
      
      const response = await httpClient.get<TicketResponse>(url);
     
      return response;
    } else {
      const response = await httpClient.get<Ticket[]>('/api/tickets');
      
      return response;
    }
  },

  async getTicketsByDateRange(params: {
    startDate: string;
    endDate: string;
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    tags?: string[];
    requestedBy?: string[];
    assignedTo?: string[];
    groups?: string[];
    categories?: string[];
    sources?: string[];
  }): Promise<TicketResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      // Format dates with Myanmar timezone (UTC+06:30) using our utility function
      // This ensures consistent timezone handling across the application
      // true for startDate sets time to 00:00:00, false for endDate sets time to 23:59:59
      queryParams.append('startDate', formatDateForApi(params.startDate, true));
      queryParams.append('endDate', formatDateForApi(params.endDate, false));
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.tags && params.tags.length > 0) {
        params.tags.forEach(tag => queryParams.append('tags[]', tag));
      }
      if (params.groups && params.groups.length > 0) {
        params.groups.forEach(group => queryParams.append('groups[]', group));
      }
      if (params.categories && params.categories.length > 0) {
        params.categories.forEach(category => queryParams.append('categories[]', category));
      }
      if (params.sources && params.sources.length > 0) {
        params.sources.forEach(source => queryParams.append('sources[]', source));
      }
      
      if (params.requestedBy && params.requestedBy.length > 0) {
        params.requestedBy.forEach(userId => {
          queryParams.append('requestedBy[]', userId);
        });
      }
      
      if (params.assignedTo && params.assignedTo.length > 0) {
        params.assignedTo.forEach(userId => {
          queryParams.append('assignedTo[]', userId);
        });
      }

      const queryString = queryParams.toString();
      const url = `/api/tickets/date-range?${queryString}`;
      
      const response = await httpClient.get<TicketResponse>(url);
      return response;
    } catch (error) {
      console.error('Error fetching tickets by date range:', error);
      // Return empty response instead of throwing error
      return {
        tickets: [],
        totalCount: 0,
        totalPages: 1,
        currentPage: params.page || 1,
        limit: params.limit || 10,
        stats: {
          total: 0,
          new: 0,
          processing: 0,
          resolved: 0,
          closed: 0,
          assigned: 0,
          unassigned: 0,
          active: 0,
          open: 0
        }
      };
    }
  },

  async searchTickets(criteria: string, value: string, page: number = 1, limit: number = 10): Promise<TicketResponse> {
    const queryParams = {
      search_criteria: criteria,
      search_value: value,
      page,
      limit
    };
    
    const response = await httpClient.get<TicketResponse>('/api/tickets/search', {
      params: queryParams
    });

    return response;
  },

  async updateStatus(ticketId: string | number, status: string, userId: number): Promise<Ticket> {
    return httpClient.put<Ticket>(`/api/tickets/${ticketId}/status`, { 
      status, 
      updated_by_id: userId 
    });
  },

  async bulkStatusUpdate (ticketIds: number[], status: string, userId: number): Promise<Ticket[]> {
    return httpClient.put<Ticket[]>(`/api/tickets/bulk-status`, { 
      ticketIds,
      status,
      updated_by_id: userId 
    });
  },

  async updateAssignee(ticketId: string | number, assignedTo: string | null, userId: number): Promise<Ticket> {
    return httpClient.put(`/api/tickets/${ticketId}/assign`, { 
      assigned_to: assignedTo,
      updated_by_id: userId 
    });
  },

  async updateTicket(ticketId: string | number, data: Partial<CreateTicketFormData>, userId: number): Promise<Ticket> {
    const requestData = {
      ...data,
      updated_by_id: userId 
    };  
    return httpClient.put<Ticket>(`/api/tickets/${ticketId}`, requestData);
  },

  async deleteTicket(ticketId: number): Promise<void> {
    return httpClient.delete(`/api/tickets/${ticketId}`);
  },

  async exportTickets(params: {
    startDate: string;
    endDate: string;
    groups?: string[];
    tags?: string[];
    status: string;
    sources?: string[];
    categories?: string[];
  }): Promise<ArrayBuffer> {
    const queryParams = new URLSearchParams();
    
    // Format dates with Myanmar timezone (UTC+06:30) using our utility function
    // This ensures consistent timezone handling across the application
    // true for startDate sets time to 00:00:00, false for endDate sets time to 23:59:59
    queryParams.append('startDate', formatDateForApi(params.startDate, true));
    queryParams.append('endDate', formatDateForApi(params.endDate, false));
    queryParams.append('status', params.status);  

    if (params.groups && params.groups.length > 0) {
      params.groups.forEach(group => queryParams.append('groups[]', group));
    }
    if (params.tags && params.tags.length > 0) {
      params.tags.forEach(tag => queryParams.append('tags[]', tag));
    }
    if (params.sources && params.sources.length > 0) {
      params.sources.forEach(source => queryParams.append('sources[]', source));
    }
    if (params.categories && params.categories.length > 0) {
      params.categories.forEach(category => queryParams.append('categories[]', category));
    }

    const queryString = queryParams.toString();
    const url = `/api/tickets/export?${queryString}`;
    
    // Get tenant ID from localStorage
    const tenantId = localStorage.getItem('tenantId');
    
    // Create headers object with tenant ID if available
    const headers: Record<string, string> = {};
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    
    const response = await httpClient.get<ArrayBuffer>(url, {
      responseType: 'arraybuffer' as const,
      headers
    });
    return response;
  },
};
